import { useState, useMemo } from 'react';
import {
  usePutVacationApprovalDoneMutation,
  useVacationDetailQuery,
  useGetVacationFile,
  usePutVacationApprovalReturnMuration,
  useGetRemainVacationByEnumberQuery,
} from '@features/vacation/hooks';
import MobileModalHeader from '@shared/components/atoms/MobileModalHeader';
import MobileVacationDiv from '@shared/components/organisms/MobileVacationDiv';
import StatusBadge from '@shared/components/atoms/StatusBadge';
import safeToBase64Image from '@shared/lib/safeToBase64Image';

import MobileInputModal from '@shared/components/atoms/MobileInputModal';
import MobileConfirm from '@shared/components/atoms/MobileConfirm';
import Alert from '@shared/components/atoms/Alert';

import styles from '@pages/vacation/components/mobile/MobileVacationApprovalContent/MobileVacationApprovalDetail.module.scss';
import clip from '@shared/assets/images/clip.svg';
import chevronRight from '@shared/assets/images/chevron-right_icon.svg';

import useToast from '@shared/hooks/useToast';
import MobileToast from '@shared/components/atoms/MobileToast';
import LoadingOverlay from '@shared/components/atoms/LoadingOverlay/LoadingOverlay';
import { renderApproverSign } from '@shared/components/atoms/RenderApproverSign';

export default function MobileVacationApprovalDetail({ onClose, vacation, onApproved }) {
  const [alertState, setAlertState] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [isRejectSheetOpen, setIsRejectSheetOpen] = useState(false);

  //토스트
  const { toastState, showToast, hideToast } = useToast();

  //결재 API
  const { mutate: approveVacation, isPending } = usePutVacationApprovalDoneMutation({
    onSuccess() {
      // 결재 성공 → 완료 Alert
      setAlertState('approve-done');
    },
    onError(error) {
      // 필요 시 에러 Alert 처리
      console.error(error);
    },
  });
  // 휴가 상세 조회 API
  const {
    data: vacationData,
    isLoading,
    isFetching,
    isError,
  } = useVacationDetailQuery(vacation.idx);
  // 파일다운로드 API
  const { mutate: downloadFile, isPending: isDownloadingFile } = useGetVacationFile();
  //   반려 API
  const { mutate: rejectVacation, isPending: isRejectPending } =
    usePutVacationApprovalReturnMuration({
      onSuccess() {
        // 반려 성공 → 모달 닫고 완료 Alert
        setIsRejectSheetOpen(false);
        setRejectReason('');
        setAlertState('reject-done');
      },
      onError(error) {
        console.error(error);
        alert('반려 처리 중 오류가 발생했습니다.');
      },
    });
  // 이미지 변환
  const { writerBase64, signerBase64 } = useMemo(() => {
    if (!vacationData) {
      return {
        writerBase64: null,
        signerBase64: null,
      };
    }
    return {
      writerBase64: vacationData.writer_sign ? safeToBase64Image(vacationData.writer_sign) : null,

      signerBase64: vacationData.signer_sign ? safeToBase64Image(vacationData.signer_sign) : null,
    };
  }, [vacationData]);

  function handleApprovalClick() {
    setAlertState('approve-confirm');
  }

  function handleFileClick(fileIdx, fileName, fileURL) {
    downloadFile({ fileIdx, fileName, fileURL });
  }

  const { data: remainVacationData } = useGetRemainVacationByEnumberQuery(vacation.enumber);
  // 휴가 현황 데이터 가공
  const vacationStatus = useMemo(
    function () {
      const raw = remainVacationData?.resultData?.remainVacation;

      if (!raw || 'NO_DATA' === raw) {
        return {
          generated: { beginningOfYear: 0, afterRevision: 0, currentYearAdditional: 0, total: 0 },
          used: { previousYearPreUsed: 0, currentYearUsed: 0, total: 0 },
          remaining: { afterRevision: 0, total: 0 },
        };
      }

      const parts = raw.split('|').map(Number);

      // 발생분
      const beginningOfYear = parts[0] ?? 0; // 년초 발생분
      const afterRevision = parts[3] ?? 0; // 개정 후 발생분
      const currentYearAdditional = parts[4] ?? 0; // 당해 추가분
      // 사용분(반영)
      const previousYearPreUsed = parts[5] ?? 0; // 전년도 선사용분
      const currentYearUsed = parts[6] ?? 0; // 당해 사용분
      // 잔여분
      const remainingAfterRevision = parts[9] ?? 0; // 개정 후
      const totalRemaining = parts[10] ?? 0; // 총 잔여분

      return {
        generated: {
          beginningOfYear: beginningOfYear,
          afterRevision: afterRevision,
          currentYearAdditional: currentYearAdditional,
          total: beginningOfYear + afterRevision + currentYearAdditional,
        },
        used: {
          previousYearPreUsed: previousYearPreUsed,
          currentYearUsed: currentYearUsed,
          total: previousYearPreUsed + currentYearUsed,
        },
        remaining: {
          afterRevision: remainingAfterRevision,
          total: totalRemaining,
        },
      };
    },
    [remainVacationData],
  );

  const isActionableState = vacationData?.state === 1 || vacationData?.state === 2;

  return (
    <>
      <div className={styles.page}>
        <MobileModalHeader title={'휴가결재'} onBack={onClose} />
        {/* 토스트 알림 (코멘트 알림 및 검증 메시지) */}
        <MobileToast
          message={toastState.message}
          isVisible={toastState.isVisible}
          onClose={hideToast}
          duration={2500}
        />
        <div className={styles.container}>
          {isDownloadingFile && <LoadingOverlay message="파일 다운로드 중..." />}
          {!isLoading && vacationData && (
            <>
              <div className={styles.haederContainer}>
                <div className={styles.headerTop}>
                  <div className={styles.headerStatus}>
                    <StatusBadge state={vacationData.state} />
                  </div>
                  <div className={styles.headerVacationRange}>
                    {vacationData.start_date} ~ {vacationData.end_date}
                  </div>
                </div>
                <div className={styles.headerBottom}>
                  <div className={styles.sginContainer}>
                    <div className={styles.signTop}>기안자</div>
                    <div className={styles.signBottom}>
                      <img
                        src={writerBase64 || undefined}
                        width={54}
                        height={54}
                        alt="신청자 전자서명 "
                      />
                    </div>
                  </div>
                  <div className={styles.sginContainer}>
                    <div className={styles.signTop}>결재자</div>
                    <div className={styles.signBottom}>
                      {renderApproverSign(vacationData.state, signerBase64, styles)}
                    </div>
                  </div>
                </div>
              </div>
              <MobileVacationDiv title={'소속'} children={vacationData.writerInfo?.team} />
              <MobileVacationDiv title={'성명'} children={vacationData.writerInfo?.name} />
              <MobileVacationDiv
                title={'신청내역'}
                children={
                  (vacationData.vacation_type?.name || '-') +
                  (vacationData.cc_type?.name ? ' - ' + vacationData.cc_type.name : '')
                }
              />
              <MobileVacationDiv
                title={'휴가기간'}
                children={
                  vacationData.start_date +
                  ' ~ ' +
                  vacationData.end_date +
                  ' (' +
                  vacationData.period +
                  '일)'
                }
              />
              <MobileVacationDiv title={'신청사유'} children={vacationData.reason} />
              <MobileVacationDiv title="첨부파일" showDivider={false}>
                {Array.isArray(vacationData.fileList) && vacationData.fileList.length > 0 ? (
                  <div className={styles.fileList}>
                    {vacationData.fileList.map((file) => (
                      <div
                        key={file.idx}
                        className={styles.fileItem}
                        onClick={() =>
                          handleFileClick(file.idx, file.name, file.url || file.fileURL)
                        }
                      >
                        <img src={clip} className={styles.clip} />
                        {file.name}
                        <img src={chevronRight} className={styles.chevronRight} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <span className={styles.emptyText}>첨부파일이 없습니다.</span>
                )}
              </MobileVacationDiv>
              <MobileVacationDiv
                title={'참조자'}
                children={
                  Array.isArray(vacationData.recipientInfos) &&
                  0 < vacationData.recipientInfos.length ? (
                    vacationData.recipientInfos.map((r) => r.name).join(', ')
                  ) : (
                    <span className={styles.emptyText}>참조자가 없습니다.</span>
                  )
                }
              />
              <MobileVacationDiv
                title={'총 휴가 잔여분'}
                children={vacationStatus.remaining.total + '일'}
              />
              {vacationData?.reject_reason && (
                <MobileVacationDiv title="코멘트" children={vacationData.reject_reason} />
              )}
              {isActionableState && (
                <div className={styles.footer}>
                  <button
                    type="button"
                    className={styles.actionButton}
                    onClick={() => setIsRejectSheetOpen(true)}
                  >
                    반려
                  </button>
                  <button
                    type="button"
                    className={`${styles.actionButton} ${styles.primaryButton}`}
                    onClick={handleApprovalClick}
                  >
                    결재
                  </button>
                </div>
              )}
            </>
          )}
          {/* 반려 바텀 시트 */}
          <MobileInputModal
            isOpen={isRejectSheetOpen}
            title="반려 사유 입력"
            value={rejectReason}
            placeholder="반려 사유를 입력해주세요."
            onChange={(value) => {
              setRejectReason(value);
            }}
            onCancel={() => {
              setIsRejectSheetOpen(false);
              setRejectReason('');
            }}
            onConfirm={() => {
              if (!rejectReason.trim()) {
                showToast('반려 사유를 입력해주세요.');
                return;
              }

              if (isRejectPending) return;

              rejectVacation({
                idx: vacation.idx,
                comment: rejectReason,
              });
            }}
          />

          {/* 반려완료  Alert */}
          <Alert
            isOpen={alertState === 'reject-done'}
            message="반려가 완료되었습니다."
            onClose={() => {
              setAlertState(null);
              onApproved?.();
            }}
          />
          {/* 결재 Alert */}
          <MobileConfirm
            isOpen={alertState === 'approve-confirm'}
            message="해당 휴가를 결재하시겠습니까?"
            confirmText="예"
            cancelText="아니요"
            onCancel={() => setAlertState(null)}
            onConfirm={() => {
              if (isPending) return;
              approveVacation({ idx: vacation.idx });
              setAlertState(null);
            }}
          />
          {/* 결제완료 Alert */}
          <Alert
            isOpen={alertState === 'approve-done'}
            message="결재가 완료되었습니다."
            onClose={() => {
              setAlertState(null);
              onApproved?.();
            }}
          />
        </div>
      </div>
    </>
  );
}
